import { useState, useEffect, useCallback, useRef } from "react";
import config from "../config";
import { useDataCache, CACHE_KEYS } from "../contexts/DataCacheContext";
import { canDonate, hasTabPermission } from "../utils/auth";

/**
 * useDonateMaterialsData
 *
 * This hook is a “data controller” for the Donate / Browse Materials table.
 * It handles:
 *  - Loading listings (with caching across page navigations via DataCacheContext)
 *  - Computing summary counters from listings (real-time as table edits occur)
 *  - Tracking unsaved changes and saving only modified rows
 *  - Adding and deleting listings
 *  - Rate-limiting refresh/save/add/delete actions (client-side UX throttling)
 *
 * SECURITY HARDENING APPLIED:
 *  1) Uses Bearer token from localStorage alongside cookie auth for reliable
 *     authentication on first page load after login.
 *
 *  2) Includes CSRF header for all mutating requests (PUT/POST/DELETE).
 *
 *  3) Optional CSRF header support (important if cookie auth is used for PUT/POST/DELETE).
 *     -> Adds X-CSRF-Token if present via meta tag or non-httpOnly cookie.
 *
 *  4) Reduce accidental sensitive data leakage in logs.
 *     -> Removes logging of full payloads/rows; logs only minimal diagnostics if needed.
 *
 *  5) Avoid surfacing raw backend error messages directly to the UI.
 *     -> Shows user-safe messages; logs details to console for developers.
 *
 *  6) AbortController used for fetch cancellation to avoid state updates after unmount.
 *
 */

/**
 * calculateCountersFromData(listingsData)
 *
 * Derives UI counters from local listings array:
 *  - total listings count
 *  - count of unique materials
 *  - expiring soon: latest_pickup between today and 7 days from now (inclusive)
 *  - overdue: latest_pickup before today
 *
 * Notes:
 *  - This is purely local computation; it does not validate date formats beyond Date parsing.
 *  - All comparisons are done at “midnight” (date-level, not time-of-day).
 */
const calculateCountersFromData = (listingsData) => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const sevenDaysFromNow = new Date(now);
  sevenDaysFromNow.setDate(now.getDate() + 7);

  const uniqueMaterials = new Set(
    (listingsData || []).map((l) => l?.material).filter(Boolean)
  ).size;

  // parse date string as local (not UTC) to avoid timezone shift
  const parseLocal = (str) => {
    if (!str) return null;
    const [y, m, d] = str.split("T")[0].split("-").map(Number);
    const date = new Date(y, m - 1, d);
    date.setHours(0, 0, 0, 0);
    return date;
  };

  // expiring soon: today through 7 days from now (inclusive)
  const expiringSoon = (listingsData || []).filter((listing) => {
    const pickupDate = parseLocal(listing?.latest_pickup);
    if (!pickupDate) return false;
    return pickupDate >= now && pickupDate <= sevenDaysFromNow;
  }).length;

  // overdue: before today
  const overdue = (listingsData || []).filter((listing) => {
    const pickupDate = parseLocal(listing?.latest_pickup);
    if (!pickupDate) return false;
    return pickupDate < now;
  }).length;

  return {
    listings: (listingsData || []).length,
    materials: uniqueMaterials,
    expiringSoon,
    overdue,
  };
};

// Client-side throttling values (UX only; not real security)
const MIN_REFRESH_INTERVAL = config.protection?.minRefreshInterval ?? 3000; // 3 sec
const MIN_SAVE_INTERVAL = config.protection?.minSaveInterval ?? 2000; // 2 sec

/**
 * Optional CSRF token retrieval.
 *
 */
function getCsrfToken() {
  const meta =
    typeof document !== "undefined" &&
    document.querySelector('meta[name="csrf-token"]');
  const metaToken = meta?.getAttribute("content");
  if (metaToken) return metaToken;

  const match =
    typeof document !== "undefined" &&
    document.cookie
      .split(";")
      .map((c) => c.trim())
      .find((c) => c.startsWith("csrf_token="));

  if (match) {
    const value = match.substring("csrf_token=".length);
    return decodeURIComponent(value);
  }

  return "";
}

// builds headers with auth token + csrf for all fetch calls
function getAuthHeaders() {
  const csrfToken = getCsrfToken();
  const accessToken = localStorage.getItem("access_token");
  return {
    "Content-Type": "application/json",
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
  };
}

// sig-based change tracking (lighter than deep-copying entire dataset)
const pickEditableFields = (row) => ({
  material: row?.material ?? null,
  category: row?.category ?? null,
  quantity: row?.quantity ?? null,
  cost: row?.cost ?? null,
  latest_pickup: row?.latest_pickup ?? null,
});

const makeSig = (row) => JSON.stringify(pickEditableFields(row));

const toId = (row) => {
  const id = row?.id ?? row?.listing_id;
  return id == null ? "" : String(id);
};

export const useDonateMaterialsData = (materialsTableRef, showAllListings = false) => {
  // -------------------------
  // Primary UI/network state
  // -------------------------
  const [loading, setLoading] = useState(true);

  const [counters, setCounters] = useState({
    listings: 0,
    materials: 0,
    expiringSoon: 0,
    overdue: 0,
  });

  const [listingsData, setListingsData] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isDummyDataLoaded, setIsDummyDataLoaded] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  // -------------------------
  // App-level cache context
  // -------------------------
  // Persists listing data across page navigations.
  const { getCachedData, setCachedData } = useDataCache();

  // Keep cache getters/setters in refs to avoid unstable dependencies inside callbacks.
  const getCachedDataRef = useRef(getCachedData);
  const setCachedDataRef = useRef(setCachedData);

  useEffect(() => {
    getCachedDataRef.current = getCachedData;
    setCachedDataRef.current = setCachedData;
  }, [getCachedData, setCachedData]);

  // -------------------------
  // Rate limiting and local caches (refs)
  // -------------------------
  const lastFetchTime = useRef(0);
  const lastRefreshTime = useRef(0);
  const lastSaveTime = useRef(0);

  const cachedRowsRef = useRef(null); // in-hook cache of last loaded rows
  const isFetching = useRef(false); // prevent concurrent fetchListings calls

  // Track which rows changed so we only PUT modified rows.
  const modifiedRows = useRef(new Set());
  const originalSigById = useRef(new Map());

  // -------------------------
  // Cache key selection (donor vs browse)
  // -------------------------
  const cacheKey = showAllListings
    ? CACHE_KEYS.BROWSE_MATERIALS
    : CACHE_KEYS.DONATE_MATERIALS;

  const cacheKeyRef = useRef(cacheKey);
  useEffect(() => {
    cacheKeyRef.current = cacheKey;
  }, [cacheKey]);

  // Keep latest showAllListings in a ref so fetchListings can remain stable.
  const showAllRef = useRef(showAllListings);
  useEffect(() => {
    showAllRef.current = showAllListings;
  }, [showAllListings]);

  // Keep an abort controller for the most recent fetch, so we can cancel on unmount
  // or when a forced refresh supersedes an in-flight request.
  const fetchAbortRef = useRef(null);

  /**
   * normalizeListingsResponse(data)
   *
   * API responses can be:
   *  - an array directly
   *  - or an object with a `listings` array field
   *
   * This helper produces a safe array in either case.
   */
  const normalizeListingsResponse = useCallback((data) => {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.listings)) return data.listings;
    return [];
  }, []);

  /**
   * applyRowsToState(rows, timestamp, fromCache)
   *
   * Centralized state application when rows are loaded:
   *  - sets listingsData
   *  - builds per-row signature map for change tracking
   *  - clears modified row set
   *  - recalculates counters
   *  - resets flags/errors
   *  - updates lastUpdated & internal fetch time
   */
  const applyRowsToState = useCallback((rows, timestamp = Date.now(), fromCache = false) => {
    const safeRows = Array.isArray(rows) ? rows : [];

    setListingsData(safeRows);
    modifiedRows.current.clear();

    // snapshot per-row signatures instead of deep-copying entire dataset
    const sigMap = new Map();
    for (const row of safeRows) {
      const id = toId(row);
      if (id) sigMap.set(id, makeSig(row));
    }
    originalSigById.current = sigMap;

    setCounters(calculateCountersFromData(safeRows));
    setHasUnsavedChanges(false);
    setErrorMessage("");
    setIsDummyDataLoaded(false);

    lastFetchTime.current = timestamp;
    setLastUpdated(timestamp);

    // Avoid logging full datasets; if you want diagnostics, keep it minimal.
    // if (fromCache) console.debug("Applied cached donate-materials rows:", safeRows.length);
  }, []);

  /**
   * fetchListings(forceRefresh)
   *
   * Loads listings from cache (if available) or from the API.
   *
   * Key behaviors:
   *  - If not forceRefresh, tries cache first and returns early if cache is valid.
   *  - Prevents concurrent fetches via isFetching ref.
   *  - Aborts previous in-flight fetch before starting a new one.
   *  - Chooses endpoint based on:
   *      * showAllListings (receiver/browse)
   *      * AND client-side permission flags (UX only; backend must enforce)
   *
   * SECURITY:
   *  - Uses both Bearer token (from localStorage) and cookie auth (credentials: "include").
   *  - Optional CSRF header is included.
   */
  const fetchListings = useCallback(
    async (forceRefresh = false) => {
      // Prevent concurrent fetches.
      if (isFetching.current) return;

      // If not forcing refresh, try cached data first.
      if (!forceRefresh) {
        const cached = getCachedDataRef.current?.(cacheKeyRef.current);
        const cachedData = cached?.data ?? cached;
        const cachedTimestamp = cached?.timestamp ?? cached?.lastUpdated ?? 0;

        const cachedRows = normalizeListingsResponse(cachedData);

        if (Array.isArray(cachedRows) && cachedRows.length > 0 && cachedTimestamp) {
          cachedRowsRef.current = cachedRows;
          applyRowsToState(cachedRows, cachedTimestamp, true);
          setLoading(false);
          return;
        }
      }

      // Mark as fetching and ensure any previous request is aborted.
      isFetching.current = true;

      try {
        // Abort any in-flight fetch before starting a new one.
        if (fetchAbortRef.current) {
          try {
            fetchAbortRef.current.abort();
          } catch (_) {
            // ignore abort failures
          }
        }
        const controller = new AbortController();
        fetchAbortRef.current = controller;

        const shouldShowAll = !!showAllRef.current;

        /**
         * Client-side endpoint selection:
         * - If showAllListings, use /api/listings
         * - Else, if user can donate + has donor permission, use /donate/listings
         * - Otherwise fall back to /api/listings
         *
         * IMPORTANT: This is UX only. Server must enforce authorization regardless.
         */
        const canUseDonorEndpoint =
          canDonate() && hasTabPermission("donor", "donate_materials");

        const url =
          shouldShowAll || !canUseDonorEndpoint
            ? `${config.apiUrl}/api/listings`
            : `${config.apiUrl}/donate/listings`;

        const response = await fetch(url, {
          method: "GET",
          headers: getAuthHeaders(),
          credentials: "include",
          signal: controller.signal,
        });

        let data = null;
        try {
          data = await response.json();
        } catch (e) {
          data = null;
        }

        if (!response.ok) {
          // Log details for developers, but keep user error messages safe.
          console.error("Failed to fetch listings:", {
            status: response.status,
            statusText: response.statusText,
            body: data,
          });

          if (response.status === 401) {
            setErrorMessage("Session expired. Please log in again.");
          } else if (response.status >= 500) {
            setErrorMessage("Server error. Please try again later.");
          } else {
            // Avoid displaying raw backend message if it may contain sensitive details.
            setErrorMessage("Failed to load listings. Please try again.");
          }
          return;
        }

        const rows = normalizeListingsResponse(data);
        const timestamp = Date.now();
        cachedRowsRef.current = rows;

        // Store in context cache (persist across page navigations).
        // Cache failures should not break the UI.
        try {
          setCachedDataRef.current?.(cacheKeyRef.current, data, timestamp);
        } catch (e) {
          // ignore cache context failures
        }

        applyRowsToState(rows, timestamp, false);
      } catch (error) {
        // Abort is not a user-visible error
        if (error?.name === "AbortError") return;

        console.error("Network error fetching listings:", error);
        setErrorMessage("Unable to connect to the server. Please check your connection.");
      } finally {
        isFetching.current = false;
        setLoading(false);
      }
    },
    [applyRowsToState, normalizeListingsResponse]
  );

  /**
   * Initial load on mount:
   *  - checks context cache first (persisted across page navigations)
   *  - otherwise fetches from API
   *
   * NOTE: Original code disables exhaustive deps; we preserve that behavior.
   */
  useEffect(() => {
    const contextCache = getCachedData(cacheKey);
    if (contextCache && contextCache.data) {
      const cachedRows = normalizeListingsResponse(contextCache.data);
      if (Array.isArray(cachedRows) && cachedRows.length > 0) {
        cachedRowsRef.current = cachedRows;
        applyRowsToState(cachedRows, contextCache.timestamp, true);
        setLoading(false);
        return;
      }
    }

    fetchListings(false);

    // Cleanup: abort in-flight fetch on unmount.
    // Reset isFetching so React StrictMode remount can retry.
    return () => {
      isFetching.current = false;
      if (fetchAbortRef.current) {
        try {
          fetchAbortRef.current.abort();
        } catch (_) {
          // ignore
        }
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * handleDataUpdate(updatedData)
   *
   * Called when the table edits produce new row data.
   * Responsibilities:
   *  - update listingsData state
   *  - compare each row's editable fields against original signature
   *  - recompute counters
   *  - update hasUnsavedChanges boolean
   */
  const handleDataUpdate = useCallback((updatedData) => {
    const safeUpdated = Array.isArray(updatedData) ? updatedData : [];

    setListingsData(safeUpdated);

    for (const row of safeUpdated) {
      const id = toId(row);
      if (!id) continue;

      const originalSig = originalSigById.current.get(id);
      const currentSig = makeSig(row);

      if (originalSig && currentSig !== originalSig)
        modifiedRows.current.add(id);
      else modifiedRows.current.delete(id);
    }

    setCounters(calculateCountersFromData(safeUpdated));
    setHasUnsavedChanges(modifiedRows.current.size > 0);
  }, []);

  /**
   * handleSaveChanges(dataOverride, workspaceChanges)
   *
   * Saves modified rows to the backend using PUT /donate/listings.
   *
   * Behaviors preserved:
   *  - blocks saving if dummy data is loaded
   *  - client-side save rate limiting
   *  - optionally applies a single workspaceChanges row before saving
   *  - only sends modified rows
   *  - keeps UI spinner visible for at least 1.5s
   *  - on success:
   *     * clears modified markers
   *     * rebuilds original signatures to match the newly saved dataset
   *     * clears table “modified cells” if supported
   *
   * SECURITY:
   *  - cookie-auth only; optional CSRF header included (important for PUT).
   */
  const handleSaveChanges = useCallback(
    async (dataOverride = null, workspaceChanges = null) => {
      const now = Date.now();

      // Prevent saving dummy data (dev-mode safety).
      if (isDummyDataLoaded) {
        console.warn("save blocked: Dummy data is currently loaded.");
        return {
          success: false,
          message:
            "Cannot save dummy data to database. Please refresh to load real data or disable Dev Mode.",
        };
      }

      // Client-side save rate limiting (UX only).
      if (now - lastSaveTime.current < MIN_SAVE_INTERVAL) {
        const waitTime = Math.ceil(
          (MIN_SAVE_INTERVAL - (now - lastSaveTime.current)) / 1000
        );
        console.warn(`save blocked: Please wait ${waitTime}s before saving again`);
        return {
          success: false,
          message: `Please wait ${waitTime} second${waitTime > 1 ? "s" : ""} before saving again.`,
        };
      }

      let dataToSave = Array.isArray(dataOverride) ? dataOverride : listingsData;

      // Apply workspaceChanges if present (preserves existing behavior).
      if (workspaceChanges?.id != null) {
        dataToSave = dataToSave.map((row) =>
          row?.id === workspaceChanges.id ? workspaceChanges : row
        );
        setListingsData(dataToSave);
        modifiedRows.current.add(String(workspaceChanges.id));
      }

      // Only send the rows that were actually modified.
      const modifiedRowsData = dataToSave.filter((row) => modifiedRows.current.has(toId(row)));
      if (modifiedRowsData.length === 0) {
        return { success: false, message: "No changes to save." };
      }

      lastSaveTime.current = now;
      setSaving(true);

      // Keep UI spinner visible briefly (original behavior).
      const minDelayPromise = new Promise((resolve) => setTimeout(resolve, 1500));

      try {
        const shouldShowAll = !!showAllRef.current;
        const url = `${config.apiUrl}/donate/listings${shouldShowAll ? "?show_all=true" : ""}`;

        const response = await fetch(url, {
          method: "PUT",
          headers: getAuthHeaders(),
          body: JSON.stringify(modifiedRowsData),
          credentials: "include",
        });

        let respJson = null;
        try {
          respJson = await response.json();
        } catch (e) {
          respJson = null;
        }

        if (!response.ok) {
          // Developer diagnostics; do not leak raw error details to end users.
          console.error("Failed to save changes:", {
            status: response.status,
            statusText: response.statusText,
            body: respJson,
          });

          await minDelayPromise;

          if (response.status === 401) {
            return { success: false, message: "Session expired. Please log in again." };
          }
          if (response.status >= 500) {
            return { success: false, message: "Server error. Please try again later." };
          }
          return { success: false, message: "Failed to save changes. Please try again." };
        }

        await minDelayPromise;

        // Success: clear modified markers and snapshot as new original data.
        setHasUnsavedChanges(false);
        setErrorMessage("");

        cachedRowsRef.current = dataToSave;

        // rebuild signatures from the saved data
        const sigMap = new Map();
        for (const row of dataToSave) {
          const id = toId(row);
          if (id) sigMap.set(id, makeSig(row));
        }
        originalSigById.current = sigMap;
        modifiedRows.current.clear();

        lastFetchTime.current = Date.now();
        setLastUpdated(lastFetchTime.current);

        // If table supports clearing “modified cells”, do it.
        if (materialsTableRef?.current?.clearModifiedCells) {
          materialsTableRef.current.clearModifiedCells();
        }

        const rowText = modifiedRowsData.length === 1 ? "row" : "rows";
        return {
          success: true,
          message:
            respJson?.message ||
            `Successfully updated ${modifiedRowsData.length} ${rowText}!`,
        };
      } catch (error) {
        console.error("Network error updating data:", error);
        await minDelayPromise;
        return {
          success: false,
          message: "Unable to connect to the server. Any changes could not be saved.",
        };
      } finally {
        setSaving(false);
      }
    },
    [listingsData, materialsTableRef, isDummyDataLoaded]
  );

  /**
   * performRefresh()
   *
   * Refreshes from the API with client-side throttling.
   * Behavior preserved:
   *  - blocks if called too soon (sets an error message)
   *  - clears local modified flags
   *  - if dummy data loaded, treat as “refreshed” without calling API
   *  - otherwise forces fetchListings(true)
   */
  const performRefresh = useCallback(async () => {
    const now = Date.now();

    if (now - lastRefreshTime.current < MIN_REFRESH_INTERVAL) {
      const waitTime = Math.ceil(
        (MIN_REFRESH_INTERVAL - (now - lastRefreshTime.current)) / 1000
      );
      console.warn(
        `refresh blocked: please wait ${waitTime}s before refreshing again`
      );
      setErrorMessage(
        `Please wait ${waitTime} second${waitTime > 1 ? "s" : ""} before refreshing again.`
      );
      return false;
    }

    lastRefreshTime.current = now;
    setErrorMessage("");
    setHasUnsavedChanges(false);
    modifiedRows.current.clear();

    if (isDummyDataLoaded) {
      return true;
    }

    await fetchListings(true);
    return true;
  }, [fetchListings, isDummyDataLoaded]);

  const handleRefresh = useCallback(async () => {
    await performRefresh();
  }, [performRefresh]);

  /**
   * handleAddListing(listingData)
   *
   * Adds a new listing via POST /donate/listings, then refreshes.
   *
   * Behavior preserved:
   *  - blocks add if dummy data loaded
   *  - client-side save throttling (reuses lastSaveTime)
   *  - on success, forces refresh
   *
   * SECURITY:
   *  - cookie-auth only; optional CSRF header included (important for POST).
   */
  const handleAddListing = useCallback(
    async (listingData) => {
      const now = Date.now();

      if (isDummyDataLoaded) {
        console.warn("Add blocked: Dummy data is currently loaded.");
        return {
          success: false,
          message: "Cannot add to dummy data. Please refresh to load real data.",
        };
      }

      if (now - lastSaveTime.current < MIN_SAVE_INTERVAL) {
        const waitTime = Math.ceil(
          (MIN_SAVE_INTERVAL - (now - lastSaveTime.current)) / 1000
        );
        return {
          success: false,
          message: `Please wait ${waitTime} second${waitTime > 1 ? "s" : ""} before adding.`,
        };
      }

      lastSaveTime.current = now;
      setSaving(true);

      try {
        const response = await fetch(`${config.apiUrl}/donate/listings`, {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify(listingData),
          credentials: "include",
        });

        let data = null;
        try {
          data = await response.json();
        } catch (e) {
          data = null;
        }

        if (response.ok) {
          await fetchListings(true);
          return { success: true, message: "Listing added successfully!" };
        }

        console.error("Failed to add listing:", {
          status: response.status,
          statusText: response.statusText,
          body: data,
        });

        if (response.status === 401) {
          return { success: false, message: "Session expired. Please log in again." };
        }
        if (response.status >= 500) {
          return { success: false, message: "Server error. Please try again later." };
        }
        return { success: false, message: "Failed to add listing. Please try again." };
      } catch (error) {
        console.error("Network error adding listing:", error);
        return { success: false, message: "Unable to connect to the server." };
      } finally {
        setSaving(false);
      }
    },
    [isDummyDataLoaded, fetchListings]
  );

  /**
   * handleDeleteListings(listingIds)
   *
   * Deletes listings via DELETE /donate/listings and then refreshes.
   *
   * Behavior preserved:
   *  - blocks delete if dummy data loaded
   *  - client-side throttling (reuses lastSaveTime)
   *  - on success, forces refresh and returns deleted_count
   *
   * SECURITY:
   *  - cookie-auth only; optional CSRF header included (important for DELETE).
   */
  const handleDeleteListings = useCallback(
    async (listingIds) => {
      const now = Date.now();

      if (isDummyDataLoaded) {
        console.warn("Delete blocked: Dummy data is currently loaded.");
        return {
          success: false,
          message: "Cannot delete dummy data. Please refresh to load real data.",
        };
      }

      if (now - lastSaveTime.current < MIN_SAVE_INTERVAL) {
        const waitTime = Math.ceil(
          (MIN_SAVE_INTERVAL - (now - lastSaveTime.current)) / 1000
        );
        return {
          success: false,
          message: `Please wait ${waitTime} second${waitTime > 1 ? "s" : ""} before deleting.`,
        };
      }

      lastSaveTime.current = now;
      setSaving(true);

      try {
        const response = await fetch(`${config.apiUrl}/donate/listings`, {
          method: "DELETE",
          headers: getAuthHeaders(),
          body: JSON.stringify({ ids: listingIds }),
          credentials: "include",
        });

        let data = null;
        try {
          data = await response.json();
        } catch (e) {
          data = null;
        }

        if (response.ok) {
          await fetchListings(true);
          return {
            success: true,
            message: `Successfully deleted ${data?.deleted_count ?? 0} listing(s)!`,
          };
        }

        console.error("Failed to delete listings:", {
          status: response.status,
          statusText: response.statusText,
          body: data,
        });

        if (response.status === 401) {
          return { success: false, message: "Session expired. Please log in again." };
        }
        if (response.status >= 500) {
          return { success: false, message: "Server error. Please try again later." };
        }
        return { success: false, message: "Failed to delete listings. Please try again." };
      } catch (error) {
        console.error("Network error deleting listings:", error);
        return { success: false, message: "Unable to connect to the server." };
      } finally {
        setSaving(false);
      }
    },
    [isDummyDataLoaded, fetchListings]
  );

  const handleBulkAddListings = useCallback(
    async (listings, batchSettings) => {
      const now = Date.now();

      if (isDummyDataLoaded) {
        return {
          success: false,
          message: "Cannot add to dummy data. Please refresh to load real data.",
        };
      }

      if (now - lastSaveTime.current < MIN_SAVE_INTERVAL) {
        const waitTime = Math.ceil(
          (MIN_SAVE_INTERVAL - (now - lastSaveTime.current)) / 1000
        );
        return {
          success: false,
          message: `Please wait ${waitTime} second${waitTime > 1 ? "s" : ""} before adding.`,
        };
      }

      lastSaveTime.current = now;
      setSaving(true);

      try {
        const response = await fetch(`${config.apiUrl}/donate/listings/bulk`, {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({ listings, batch_settings: batchSettings }),
          credentials: "include",
        });

        let data = null;
        try {
          data = await response.json();
        } catch (e) {
          data = null;
        }

        if (response.ok) {
          await fetchListings(true);
          return {
            success: true,
            message: `Successfully created ${data?.count ?? listings.length} listing(s)!`,
          };
        }

        if (response.status === 401) {
          return { success: false, message: "Session expired. Please log in again." };
        }
        if (response.status === 400) {
          return { success: false, message: data?.message || "Validation failed." };
        }
        if (response.status >= 500) {
          return { success: false, message: "Server error. Please try again later." };
        }
        return { success: false, message: "Failed to create listings. Please try again." };
      } catch (error) {
        console.error("Network error in bulk add:", error);
        return { success: false, message: "Unable to connect to the server." };
      } finally {
        setSaving(false);
      }
    },
    [isDummyDataLoaded, fetchListings]
  );

  // toggle listing visibility (pause/unpause from marketplace)
  const handleToggleVisibility = useCallback(
    async (listingIds, shouldPause) => {
      const now = Date.now();

      if (isDummyDataLoaded) {
        return { success: false, message: "Cannot modify dummy data. Please refresh to load real data." };
      }

      if (now - lastSaveTime.current < MIN_SAVE_INTERVAL) {
        const waitTime = Math.ceil((MIN_SAVE_INTERVAL - (now - lastSaveTime.current)) / 1000);
        return { success: false, message: `Please wait ${waitTime} second${waitTime > 1 ? "s" : ""} before toggling.` };
      }

      lastSaveTime.current = now;
      setSaving(true);

      try {
        const payload = listingIds.map((id) => ({
          id,
          status: shouldPause ? "Paused" : "New",
        }));

        const response = await fetch(`${config.apiUrl}/donate/listings`, {
          method: "PUT",
          headers: getAuthHeaders(),
          body: JSON.stringify(payload),
          credentials: "include",
        });

        if (response.ok) {
          await fetchListings(true);
          const action = shouldPause ? "hidden from" : "restored to";
          return {
            success: true,
            message: `${listingIds.length} listing(s) ${action} marketplace.`,
          };
        }

        if (response.status === 401) return { success: false, message: "Session expired. Please log in again." };
        if (response.status >= 500) return { success: false, message: "Server error. Please try again later." };
        return { success: false, message: "Failed to update listing visibility." };
      } catch (error) {
        console.error("Network error toggling visibility:", error);
        return { success: false, message: "Unable to connect to the server." };
      } finally {
        setSaving(false);
      }
    },
    [isDummyDataLoaded, fetchListings]
  );

  // -------------------------
  // Public API (returned object)
  // -------------------------
  return {
    loading,
    counters,
    listingsData,
    hasUnsavedChanges,
    saving,
    errorMessage,
    isDummyDataLoaded,
    lastUpdated,

    // setters (used elsewhere)
    setCounters,
    setListingsData,
    setLoading,
    setErrorMessage,
    setHasUnsavedChanges,
    setIsDummyDataLoaded,

    /**
     * setOriginalData(data)
     *
     * External setter that rebuilds the original signature map.
     */
    setOriginalData: (data) => {
      const rows = Array.isArray(data) ? data : [];
      const sigMap = new Map();
      for (const row of rows) {
        const id = toId(row);
        if (id) sigMap.set(id, makeSig(row));
      }
      originalSigById.current = sigMap;
    },

    // actions
    handleDataUpdate,
    handleSaveChanges,
    handleRefresh,
    performRefresh,
    handleAddListing,
    handleBulkAddListings,
    handleDeleteListings,
    handleToggleVisibility,

    // optional: expose fetch if needed elsewhere
    fetchListings,
  };
};